// Keep the x-axis caption clear of the tick-label band AFTER furigana is added.
//
// The caption is positioned during render from the axis group's measured
// extent. That measurement happens before renderSvgFurigana runs, because the
// reading aid is a post-render pass over the emitted <text>. The ruby <tspan>s
// it adds are frequently WIDER than the kanji they sit over (東京 -> とうきょう)
// and they inherit the tick rotation, so the band grows downward after the
// caption has already been placed — and the caption can end up sitting on the
// readings even though the pre-furigana layout was correct.
//
// Rather than teach every renderer about furigana, this runs once after the
// reading pass and nudges the caption down if the band has grown. It only ever
// moves the caption DOWN, so a chart that was already clear is untouched.
//
// The readings can also be removed or re-applied without a redraw (the engine
// arriving late, the toggle). The first move records the render-time position so
// resetAxisTitles can put the caption back before the band is measured again —
// otherwise a caption pushed down for readings would stay down once they are gone.

const X_AXIS_SELECTOR = '.axis-x, [data-element-id="axis_x"]';
const X_TITLE_SELECTOR = '[data-axis-title="x"]';
const RENDER_Y_ATTR = 'data-axis-title-render-y';

function safeBBox(node) {
  if (!node || typeof node.getBBox !== 'function') return null;
  try {
    const box = node.getBBox();
    // A detached or display:none subtree reports an all-zero box; moving the
    // caption from that would be worse than leaving it where render put it.
    if (!box || (box.width === 0 && box.height === 0)) return null;
    return box;
  } catch (e) {
    return null; // getBBox throws on unrendered SVG in some engines
  }
}

function svgHeight(svgNode) {
  const attr = parseFloat(svgNode.getAttribute('height'));
  if (Number.isFinite(attr) && attr > 0) return attr;
  const box = safeBBox(svgNode);
  return box ? box.y + box.height : null;
}

/**
 * @param {SVGSVGElement|null} svgNode
 * @param {number} gapPx extra breathing room under the lowest tick glyph
 * @returns {number} how many captions were moved (0 when nothing needed it)
 */
export function realignAxisTitles(svgNode, gapPx = 6) {
  if (!svgNode || typeof svgNode.querySelectorAll !== 'function') return 0;

  const titles = Array.from(svgNode.querySelectorAll(X_TITLE_SELECTOR));
  if (!titles.length) return 0;

  const axis = svgNode.querySelector(X_AXIS_SELECTOR);
  const axisBox = safeBBox(axis);
  if (!axisBox) return 0;

  // getBBox is local to the axis <g>, which is translated to the axis line;
  // convert its bottom into the caption's own coordinate space.
  let axisTop = 0;
  const transform = axis.getAttribute('transform') || '';
  const match = /translate\(\s*[-\d.]+\s*[, ]\s*([-\d.]+)/.exec(transform);
  if (match) axisTop = parseFloat(match[1]) || 0;
  const bandBottom = axisTop + axisBox.y + axisBox.height;

  let moved = 0;
  titles.forEach((title) => {
    const titleBox = safeBBox(title);
    if (!titleBox) return;
    const fontSize = parseFloat(window.getComputedStyle(title).fontSize) || 20;
    let wanted = bandBottom + gapPx + fontSize;
    const current = parseFloat(title.getAttribute('y'));
    if (!Number.isFinite(current) || wanted <= current) return; // never move up

    // Render-time placement clamps to the canvas; this later correction must
    // not quietly drop that. Pushing the caption out of the viewport trades a
    // readable overlap for a caption nobody can see at all, which is worse.
    const limit = svgHeight(svgNode);
    if (Number.isFinite(limit)) {
      wanted = Math.min(wanted, limit - 4);
      if (wanted <= current) return; // no room to improve without going outside
    }
    if (!title.hasAttribute(RENDER_Y_ATTR)) title.setAttribute(RENDER_Y_ATTR, String(current));
    title.setAttribute('y', String(wanted));
    moved += 1;
  });
  return moved;
}

/**
 * Put every caption realignAxisTitles moved back at its render-time position.
 * @param {SVGSVGElement|null} svgNode
 * @returns {number} how many captions were restored
 */
export function resetAxisTitles(svgNode) {
  if (!svgNode || typeof svgNode.querySelectorAll !== 'function') return 0;
  const moved = Array.from(svgNode.querySelectorAll(`${X_TITLE_SELECTOR}[${RENDER_Y_ATTR}]`));
  moved.forEach((title) => {
    title.setAttribute('y', title.getAttribute(RENDER_Y_ATTR));
    title.removeAttribute(RENDER_Y_ATTR);
  });
  return moved.length;
}

export default realignAxisTitles;
